import { useMediaQuery } from "@mui/material";
import { getDefaultApplicationGuideContent } from "pages/customForms/components/DefaultGuides";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  createGuideDeliberatePractice,
  deleteGuideDeliberatePractice,
  getGuideDeliberatePractices,
  updateGuideDeliberatePractice,
} from "reduxStore/actions/guideDeliberatePracticeActions";
import { checkRoles } from "utils/permissions";
import { v4 as uuidv4 } from "uuid";
import Tracking from "assets/images/Tracking.png";

export const useDeliberatePracticeGuide = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const user = useSelector((state) => state.user);
  const { isSystemAdmin } = checkRoles(user);
  const guides = useSelector((state) => state.guideDeliberatePractices);

  const [openGuideEdit, setOpenGuideEdit] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [description, setDescription] = useState("");
  const [guideId, setGuideId] = useState("");
  const [isLoadingGuides, setIsLoadingGuides] = useState(true);

  const containerRefs = useRef([]);
  const defaultGuideContent = getDefaultApplicationGuideContent;
  const defaultImageURL = Tracking;

  const handleOpenGuideEdit = () => setOpenGuideEdit(true);
  const handleCloseGuideEdit = () => setOpenGuideEdit(false);

  const preprocessContentForSave = (content) =>
    content.replace(/<li data-list="bullet">/g, "<li>");

  const sanitizeContent = (htmlContent) => {
    const updatedContent = DOMPurify.sanitize(htmlContent).replace(
      /(<p><br><\/p>)+/g,
      "<br>"
    );
    return { __html: updatedContent };
  };

  const handleSaveGuide = async () => {
    try {
      const guideData = {
        title: "Deliberate Practice Guide",
        description,
        content: contentBlocks.map((block) =>
          block.type === "text"
            ? { ...block, data: preprocessContentForSave(block.data) }
            : block
        ),
      };

      const action = guideId
        ? updateGuideDeliberatePractice({ ...guideData, _id: guideId })
        : createGuideDeliberatePractice(guideData);

      const result = await dispatch(action);
      if (result) setGuideId(result._id);
      handleCloseGuideEdit();
    } catch (error) {
      console.error("Error saving guide:", error);
    }
  };

  const handleDeleteGuide = async () => {
    if (window.confirm("Are you sure you want to delete this guide?")) {
      const success = await dispatch(deleteGuideDeliberatePractice(guideId));
      if (success) {
        setGuideId("");
        setContentBlocks(defaultGuideContent);
        alert("Guide deleted successfully");
      } else {
        alert("Failed to delete guide");
      }
    }
  };

  const addTextBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "text", data: "" },
    ]);

  const addImageBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "image", data: null },
    ]);

  const updateBlockData = (index, data) => {
    if (index === -1 && Array.isArray(data)) {
      setContentBlocks(data);
    } else {
      setContentBlocks((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], data };
        return updated;
      });
    }
  };

  const removeBlock = (index) =>
    setContentBlocks((prev) => prev.filter((_, i) => i !== index));

  const swapPublicToXxLarge = (url) =>
    url ? url.replace(/\/public$/, "/xxLarge") : defaultImageURL;

  useEffect(() => {
    const fetchGuides = async () => {
      setIsLoadingGuides(true);
      await dispatch(getGuideDeliberatePractices());
      setIsLoadingGuides(false);
    };
    fetchGuides();
  }, [dispatch]);

  useEffect(() => {
    if (!isLoadingGuides) {
      const guide = guides.find((g) => g.title === "Deliberate Practice Guide");
      if (guide) {
        setContentBlocks(
          (guide.content || []).map((block) => ({
            ...block,
            id: block.id || uuidv4(),
          }))
        );
        setDescription(guide.description);
        setGuideId(guide._id);
      } else {
        setContentBlocks(defaultGuideContent);
        setDescription("Default Deliberate Practice Guide");
        setGuideId("");
      }
    }
  }, [guides, isLoadingGuides]);

  useEffect(() => {
    contentBlocks.forEach((block, index) => {
      if (block.type === "text" && containerRefs.current[index]) {
        containerRefs.current[index].innerHTML = block.data;
      }
    });
  }, [contentBlocks]);

  return {
    isLargeScreen,
    isSystemAdmin,
    openGuideEdit,
    handleOpenGuideEdit,
    handleCloseGuideEdit,
    handleSaveGuide,
    handleDeleteGuide,
    addTextBlock,
    addImageBlock,
    updateBlockData,
    removeBlock,
    contentBlocks,
    sanitizeContent,
    isLoadingGuides,
    defaultImageURL,
    guideId,
    swapPublicToXxLarge,
  };
};
